import React from "react";

import { cn } from "@/lib/utils";

/**
 * The app's on/off switch.
 *
 * One component, because the same 24×44 pill was written out three times — org
 * settings, prompt safety and the fleet kill switch — and each copy carried its
 * own literal colours.
 *
 * Colours are theme tokens: `tone-emerald` for on, `tone-slate` for off. The
 * knob takes the theme's own surface rather than a literal white, which is what
 * keeps it visible on the pale tone the dark theme uses for both tracks.
 *
 * `readOnly` renders the state as a pill instead of a control. A viewer without
 * edit permission gets the value, never a switch that can only bounce off a
 * 403 — and never a disabled control they cannot tell from one mid-save.
 *
 * @param {boolean}  checked
 * @param {string}   label       what the switch governs; read by screen readers
 * @param {boolean}  [readOnly]  render the state, with no control
 * @param {string}   [readOnlyText] override the pill text (e.g. "Unavailable")
 * @param {boolean}  [readOnlyMuted] render the pill neutral even when checked
 * @param {"md"|"sm"} [size]       "sm" for a compact track, e.g. inline on a card
 *
 * Any other prop — `id`, `title`, a `data-` attribute — lands on the rendered
 * element, so a caller can bind its own `<label htmlFor>` to the control.
 */
export const Switch = ({
  checked,
  disabled = false,
  onToggle,
  label = "",
  readOnly = false,
  readOnlyText,
  readOnlyMuted = false,
  size = "md",
  className = "",
  ...props
}) => {
  if (readOnly) {
    const on = checked && !readOnlyMuted;
    return (
      <span
        {...props}
        className={cn(
          "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium",
          on ? "bg-tone-emerald-soft text-tone-emerald" : "bg-tone-slate-soft text-text-secondary",
          className,
        )}
      >
        {readOnlyText ?? (checked ? "On" : "Off")}
      </span>
    );
  }

  const isSm = size === "sm";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onToggle}
      {...props}
      className={cn(
        "relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-tone-blue focus:ring-offset-2 focus:ring-offset-background-primary disabled:cursor-not-allowed disabled:opacity-50",
        isSm ? "h-4 w-7" : "h-6 w-11",
        checked ? "bg-tone-emerald" : "bg-tone-slate",
        className,
      )}
    >
      <span className="sr-only">Toggle {label}</span>
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none inline-block transform rounded-full bg-background-secondary shadow ring-0 transition duration-200 ease-in-out",
          isSm ? "h-3 w-3" : "h-5 w-5",
          checked ? (isSm ? "translate-x-3" : "translate-x-5") : "translate-x-0",
        )}
      />
    </button>
  );
};

export default Switch;
